import { UrlReturnToStack } from "@src/hooks/useReturnTo/UrlReturnToStack";
import type { FaqAnchorType } from "@src/pages/faq";
import networkStore from "@src/store/networkStore";

export type ConfigureDeploymentParams = {
  dseq?: string | number;
  templateId?: string;
  userTemplateId?: string;
  sdlStrategy?: "default" | "edit";
  bidStrategy?: "auto" | "select";
  draftId?: string;
  vm?: boolean;
  exampleImage?: boolean;
};

export const domainName = "https://console.akash.network";
export function getBaseUrl(): string {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return domainName;
}

type ReturnableOptions = { returnTo?: string };
const getSafeCurrentLocation = (preferredLocation?: string, fallbackLocation: string = "/") => {
  if (preferredLocation) {
    return preferredLocation;
  }

  if (typeof window !== "undefined") {
    return `${window.location.pathname}${window.location.search}`;
  }

  return fallbackLocation;
};

const getSafeReturnableUrl = (destination: string, currentLocation?: string, extraReturnToParams: Record<string, string> = {}) =>
  UrlReturnToStack.createReturnable(getSafeCurrentLocation(currentLocation), destination, { extraQueryParams: extraReturnToParams });

export const UrlService = {
  home: () => "/deployments",
  getStarted: () => "/get-started",

  priceCompare: () => "/price-compare",
  analytics: () => "/analytics",
  graph: (snapshot: string) => `/graph/${snapshot}`,
  providerGraph: (snapshot: string) => `/provider-graph/${snapshot}`,
  priceCompareCustom: (cpu: number, memory: number, storage: number, memoryUnit: string, storageUnit: string) =>
    `/price-compare${appendSearchParams({ cpu, memory, storage, memoryUnit, storageUnit })}`,
  contact: () => "/contact",
  faq: (q?: FaqAnchorType) => `/faq${q ? "#" + q : ""}`,
  privacyPolicy: () => "/privacy-policy",
  termsOfService: () => "/terms-of-service",
  prohibitedUse: () => "/terms-of-service#prohibited-use",

  // User
  userSettings: () => "/user/settings",
  userApiKeys: () => "/user/api-keys",
  userFavorites: () => `/user/settings/favorites`,
  userProfile: (username: string) => `/profile/${username}`,
  usage: () => "/usage",
  billing: ({ openPayment, setupAutoTopUp }: { openPayment?: boolean; setupAutoTopUp?: boolean } = {}) =>
    `/billing${appendSearchParams({ openPayment, setupAutoTopUp })}`,
  /** @deprecated use .newLogin instead */
  login: () => "/api/auth/login",
  /** @deprecated use .newSignup instead */
  signup: () => "/api/auth/signup",
  newLogin: ({ returnTo }: ReturnableOptions = {}) => getSafeReturnableUrl(`/login${appendSearchParams({ tab: "login" })}`, returnTo),
  newSignup: ({ returnTo, ...extraReturnToParams }: ReturnableOptions & Record<string, string> = {}) =>
    getSafeReturnableUrl(`/login${appendSearchParams({ tab: "signup" })}`, returnTo, extraReturnToParams),
  logout: () => "/api/auth/logout",
  template: (id: string) => `/template/${id}`,

  // Deploy
  onboardingPicker: ({ redeemCoupon, intent }: { redeemCoupon?: boolean; intent?: string } = {}) =>
    `/onboarding${appendSearchParams({ redeemCoupon, intent })}`,
  deploymentList: () => `/deployments`,
  activity: () => "/activity",
  deploymentDetails: (dseq: string, tab?: string, logsMode?: string) => `/deployments/${dseq}${appendSearchParams({ tab, logsMode })}`,
  templates: (category?: string | null, search?: string) => `/templates${appendSearchParams({ category, search })}`,
  templateDetails: (templateId: string) => `/templates/${templateId}`,
  providers: (sort?: string) => `/providers${appendSearchParams({ sort })}`,
  providerDetail: (owner: string) => `/providers/${owner}${appendSearchParams({ network: networkStore.selectedNetworkId })}`,
  alerts: () => "/alerts",

  // Organizations
  organizationSettings: () => "/organization",
  members: () => "/members",
  invitations: () => "/invitations",
  projects: () => "/projects",
  project: (projectId: string) => `/projects/${projectId}`,

  newDeployment: ({ projectId }: { projectId?: string } = {}) => `/new-deployment${appendSearchParams({ projectId })}`,

  configureDeployment: (params: ConfigureDeploymentParams = {}) => {
    const { dseq, templateId, userTemplateId, sdlStrategy, bidStrategy, draftId, vm, exampleImage } = params;
    const base = dseq ? `/new-deployment/configure/${dseq}` : "/new-deployment/configure";
    return `${base}${appendSearchParams({ templateId, userTemplateId, "sdl-strategy": sdlStrategy, "bid-strategy": bidStrategy, draftId, vm, exampleImage })}`;
  }
};

export function appendSearchParams(params: { [key: string]: string | number | boolean | null | undefined } = {}) {
  const urlParams = new URLSearchParams("");
  Object.keys(params).forEach(p => {
    const value = params[p];
    if (value) {
      urlParams.set(p, value.toString());
    }
  });

  const res = urlParams.toString();

  return res ? `?${res}` : res;
}

export function removeEmptyFilters(obj: { [key: string]: string }) {
  const copy = { ...obj };
  Object.keys(copy).forEach(key => {
    if (copy[key] === "*") {
      delete copy[key];
    }
  });

  return copy;
}

export function handleDocClick(ev: React.MouseEvent<any>, url: string): void {
  ev.preventDefault();

  window.open(url, "_blank");
}
